import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import Start from "./Start";
import { initialize } from "../store/verification.slice";
import { IS_DEV, parseAllowedParentOrigin } from "./utils";
import Loading from "./reusable/Loading";
import Permission from "./Permission";
import MobileDevicePrompt from "./MobileDevicePrompt";
import Error from "./reusable/Error";
import Verification from "./Verification";
import { Card, CardBody } from "@nextui-org/react";
import ClosePrompt from "./reusable/ClosePrompt";
import { ThemeProvider } from "../theme/ThemeProvider";
import { parseThemeQueryParam } from "../theme/validateTheme";

function Home() {
  const {
    cameraPermission,
    loading,
    closeInitiated,
    error,
    isMobileDevice,
    verification,
    credentials,
    orgTheme,
  } = useSelector((store) => store.verification);
  const dispatch = useDispatch();

  const search = window.location.search;
  const params = new URLSearchParams(search);
  const flowId = params.get("flowId");
  const clientId = params.get("clientId");
  const verificationId = params.get("verificationId");
  const redirect = params.get("redirect");
  const metadata = (() => {
    const raw = params.get("metadata");
    if (raw == null) return null;
    if (raw.length > 2048) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  })();
  const themeOverride = parseThemeQueryParam(params.get("theme"));

  // Side-effect-free preview mode for the dashboard's branding preview —
  // unconditional (not IS_DEV-gated like `debug` below), since it must work
  // against the production direct-link deployment. Skips the token exchange
  // entirely: no network call at all, just the themed Start screen with its
  // CTA turned into a no-op (see Start.jsx). Never set by real customer
  // embeds — urtentic.js has no knowledge of this param.
  const isPreview = params.get("preview") === "true";

  // Live-theme-update postMessage channel is dashboard-preview-only — the
  // real SDK embed never needs it (its theme is set once at load and never
  // changes), so ThemeProvider only registers the listener when this is set.
  const expectedParentOrigin = isPreview
    ? parseAllowedParentOrigin(params.get("parentOrigin"))
    : null;

  // StrictMode re-runs this effect in development (mount, cleanup, mount) on
  // the same component instance, which would fire a second keys/exchange per
  // page load — doubling calls against core's per-IP rate limit on that
  // endpoint. The ref survives that re-run, so initialize fires once per
  // flowId/clientId pair, same as in production.
  const initializedKeyRef = useRef(null);

  useEffect(() => {
    if (isPreview) return;
    const initKey = `${flowId}:${clientId}`;
    if (initializedKeyRef.current === initKey) return;
    initializedKeyRef.current = initKey;
    dispatch(initialize(flowId, clientId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, dispatch, flowId, isPreview]);

  const isDebug = IS_DEV && params.get("debug") === "true";

  const debugVerification = {
    verificationId: "debug-verification-id",
    identityId: "debug-identity-id",
    inputs: [{ fieldName: "DOCUMENT_PHOTO" }],
    settings: [
      { fieldName: "GALLERY_RESTRICTION", value: "false" },
      { fieldName: "RESTRICT_SCREENSHOT", value: "false" },
      { fieldName: "DOCUMENT_TYPES", value: JSON.stringify([{ step: 1 }]) },
      { fieldName: "BIOMETRICS", value: "SELFIE_VIDEO" },
    ],
    allowedRedirectUrls: [],
  };

  const activeVerification = isDebug ? debugVerification : verification;

  // ---- Determine which view to show ----
  let view = "loading";

  if (closeInitiated) {
    view = "close";
  } else if (isDebug) {
    view = "verification";
  } else if (loading) {
    view = "loading";
  } else if (error) {
    view = "error";
  } else if (!verification) {
    view = "start";
  } else if (!isMobileDevice) {
    view = "mobilePrompt";
  } else if (cameraPermission === "") {
    view = "checkingPermission";
  } else if (cameraPermission !== "granted") {
    view = "permission";
  } else if (verification && cameraPermission === "granted" && isMobileDevice) {
    view = "verification";
  }

  return (
    <ThemeProvider
      orgTheme={orgTheme}
      override={themeOverride}
      expectedParentOrigin={expectedParentOrigin}
    >
      <main data-testid="app">
        <div className="app-viewport-height h-dvh w-dvw bg-gray-800 flex flex-col justify-center items-center">
          <Card
            radius="none"
            fullWidth
            id="card"
            className="background app-viewport-height w-full h-dvh xs:max-w-[400px] xs:rounded-small xs:m-10 bg-white"
          >
            <CardBody className="p-0 h-full">
              {view === "loading" && <Loading minimal />}
              {view === "close" && <ClosePrompt verification={verification} />}
              {view === "error" && <Error />}
              {view === "start" && (
                <Start
                  verificationId={verificationId}
                  flowId={flowId}
                  redirectUrl={redirect}
                  clientId={clientId}
                  metadata={metadata}
                  previewMode={isPreview}
                />
              )}
              {view === "mobilePrompt" && (
                <MobileDevicePrompt
                  redirectUrl={redirect}
                  clientId={clientId}
                  verification={verification}
                  accessToken={credentials?.accessToken}
                />
              )}
              {view === "checkingPermission" && <Loading />}
              {view === "permission" && (
                <Permission flowId={flowId} clientId={clientId} metadata={metadata} />
              )}
              {view === "verification" && (
                <Verification
                  verification={activeVerification}
                  redirectUrl={redirect}
                  clientId={clientId}
                  credentials={credentials}
                />
              )}
            </CardBody>
          </Card>
        </div>
      </main>
    </ThemeProvider>
  );
}

export default Home;
